import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import {
  deleteProductFeatureChildApi,
  deleteWebsiteProductFeatureOrderProcessChildApi,
  getProductFeatureChildApi,
  getOrderProcessChildByWebsiteProductFeatureApi,
  deleteWebsiteProductFeatureApi,
  getWebsiteProductFeatureOrderProcessChildApi,
  getWebsiteProductFeatureApi,
  postProductFeatureChildApi,
  postWebsiteProductFeatureOrderProcessChildApi,
  postWebsiteProductFeatureApi,
  updateProductFeatureChildApi,
  updateWebsiteProductFeatureOrderProcessChildApi,
  updateWebsiteProductFeatureApi,
} from "../../api/common/WebsiteProductFeatureApi";

const initialState = {
  websiteProductFeature: null,
  productFeatureChildren: [],
  orderProcessChild: [],
  selectedOrderProcessChild: null,
  loading: false,
  productFeatureChildLoading: false,
  productFeatureChildSaving: false,
  productFeatureChildDeleting: false,
  childLoading: false,
  saving: false,
  childSaving: false,
  deleting: false,
  childDeleting: false,
  error: null,
  success: false,
};

const getErrorPayload = (err) => ({
  errorMessage:
    err?.data?.message ||
    err?.response?.data?.message ||
    err?.response?.data?.error ||
    err?.message ||
    "Something went wrong",
});

const getResponsePayload = (payload) => payload?.response ?? payload;

const getOrderProcessChildItems = (payload) => {
  const response = getResponsePayload(payload);

  if (Array.isArray(response)) return response;
  if (Array.isArray(response?.orderProcessChild)) return response.orderProcessChild;
  if (Array.isArray(response?.orderProcessChildren))
    return response.orderProcessChildren;
  if (Array.isArray(response?.items)) return response.items;
  if (Array.isArray(response?.content)) return response.content;

  return [];
};

const getProductFeatureChildItems = (payload) => {
  const response = getResponsePayload(payload);

  if (Array.isArray(response)) return response;
  if (Array.isArray(response?.productFeatureChildren))
    return response.productFeatureChildren;
  if (Array.isArray(response?.productFeatureChild))
    return response.productFeatureChild;
  if (Array.isArray(response?.items)) return response.items;
  if (Array.isArray(response?.content)) return response.content;

  return response ? [response] : [];
};

const getWebsiteProductFeaturePayload = (payload) => {
  const feature = getResponsePayload(payload);

  if (!feature || Array.isArray(feature)) return feature;

  return {
    ...feature,
    orderProcessChild: getOrderProcessChildItems(payload),
  };
};

export const getWebsiteProductFeature = createAsyncThunk(
  "websiteProductFeature/getWebsiteProductFeature",
  async (payload, { rejectWithValue }) => {
    try {
      const response = await getWebsiteProductFeatureApi(payload);
      return response?.data;
    } catch (err) {
      return rejectWithValue(getErrorPayload(err));
    }
  },
);

export const postWebsiteProductFeature = createAsyncThunk(
  "websiteProductFeature/postWebsiteProductFeature",
  async (payload, { rejectWithValue }) => {
    try {
      const response = await postWebsiteProductFeatureApi(payload);
      return response?.data;
    } catch (err) {
      return rejectWithValue(getErrorPayload(err));
    }
  },
);

export const updateWebsiteProductFeature = createAsyncThunk(
  "websiteProductFeature/updateWebsiteProductFeature",
  async (payload, { rejectWithValue }) => {
    try {
      const response = await updateWebsiteProductFeatureApi(payload);
      return response?.data;
    } catch (err) {
      return rejectWithValue(getErrorPayload(err));
    }
  },
);

export const deleteWebsiteProductFeature = createAsyncThunk(
  "websiteProductFeature/deleteWebsiteProductFeature",
  async (payload, { rejectWithValue }) => {
    try {
      const response = await deleteWebsiteProductFeatureApi(payload);
      return response?.data;
    } catch (err) {
      return rejectWithValue(getErrorPayload(err));
    }
  },
);

export const getWebsiteProductFeatureOrderProcessChild = createAsyncThunk(
  "websiteProductFeature/getWebsiteProductFeatureOrderProcessChild",
  async (payload, { rejectWithValue }) => {
    try {
      const response =
        await getWebsiteProductFeatureOrderProcessChildApi(payload);
      return response?.data;
    } catch (err) {
      return rejectWithValue(getErrorPayload(err));
    }
  },
);

export const getOrderProcessChildByWebsiteProductFeature = createAsyncThunk(
  "websiteProductFeature/getOrderProcessChildByWebsiteProductFeature",
  async (payload, { rejectWithValue }) => {
    try {
      const response =
        await getOrderProcessChildByWebsiteProductFeatureApi(payload);
      return response?.data;
    } catch (err) {
      return rejectWithValue(getErrorPayload(err));
    }
  },
);

export const postWebsiteProductFeatureOrderProcessChild = createAsyncThunk(
  "websiteProductFeature/postWebsiteProductFeatureOrderProcessChild",
  async (payload, { rejectWithValue }) => {
    try {
      const response =
        await postWebsiteProductFeatureOrderProcessChildApi(payload);
      return response?.data;
    } catch (err) {
      return rejectWithValue(getErrorPayload(err));
    }
  },
);

export const updateWebsiteProductFeatureOrderProcessChild = createAsyncThunk(
  "websiteProductFeature/updateWebsiteProductFeatureOrderProcessChild",
  async (payload, { rejectWithValue }) => {
    try {
      const response =
        await updateWebsiteProductFeatureOrderProcessChildApi(payload);
      return response?.data;
    } catch (err) {
      return rejectWithValue(getErrorPayload(err));
    }
  },
);

export const deleteWebsiteProductFeatureOrderProcessChild = createAsyncThunk(
  "websiteProductFeature/deleteWebsiteProductFeatureOrderProcessChild",
  async (payload, { rejectWithValue }) => {
    try {
      const response =
        await deleteWebsiteProductFeatureOrderProcessChildApi(payload);
      return response?.data;
    } catch (err) {
      return rejectWithValue(getErrorPayload(err));
    }
  },
);

export const getProductFeatureChild = createAsyncThunk(
  "websiteProductFeature/getProductFeatureChild",
  async (payload, { rejectWithValue }) => {
    try {
      const response = await getProductFeatureChildApi(payload);
      return response?.data;
    } catch (err) {
      return rejectWithValue(getErrorPayload(err));
    }
  },
);

export const postProductFeatureChild = createAsyncThunk(
  "websiteProductFeature/postProductFeatureChild",
  async (payload, { rejectWithValue }) => {
    try {
      const response = await postProductFeatureChildApi(payload);
      return response?.data;
    } catch (err) {
      return rejectWithValue(getErrorPayload(err));
    }
  },
);

export const updateProductFeatureChild = createAsyncThunk(
  "websiteProductFeature/updateProductFeatureChild",
  async (payload, { rejectWithValue }) => {
    try {
      const response = await updateProductFeatureChildApi(payload);
      return response?.data;
    } catch (err) {
      return rejectWithValue(getErrorPayload(err));
    }
  },
);

export const deleteProductFeatureChild = createAsyncThunk(
  "websiteProductFeature/deleteProductFeatureChild",
  async (payload, { rejectWithValue }) => {
    try {
      const response = await deleteProductFeatureChildApi(payload);
      return response?.data;
    } catch (err) {
      return rejectWithValue(getErrorPayload(err));
    }
  },
);

const websiteProductFeatureSlice = createSlice({
  name: "websiteProductFeature",
  initialState,
  reducers: {
    clearWebsiteProductFeatureError: (state) => {
      state.error = null;
    },
    clearWebsiteProductFeatureState: (state) => {
      state.websiteProductFeature = null;
      state.productFeatureChildren = [];
      state.orderProcessChild = [];
      state.selectedOrderProcessChild = null;
      state.loading = false;
      state.productFeatureChildLoading = false;
      state.productFeatureChildSaving = false;
      state.productFeatureChildDeleting = false;
      state.childLoading = false;
      state.saving = false;
      state.childSaving = false;
      state.deleting = false;
      state.childDeleting = false;
      state.error = null;
      state.success = false;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getWebsiteProductFeature.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.success = false;
      })
      .addCase(getWebsiteProductFeature.fulfilled, (state, action) => {
        state.websiteProductFeature = getWebsiteProductFeaturePayload(
          action?.payload,
        );
        state.orderProcessChild = getOrderProcessChildItems(action?.payload);
        state.loading = false;
        state.success = true;
      })
      .addCase(getWebsiteProductFeature.rejected, (state, action) => {
        state.loading = false;
        state.error = action?.payload?.errorMessage || null;
        state.success = false;
      })
      .addCase(postWebsiteProductFeature.pending, (state) => {
        state.saving = true;
        state.error = null;
        state.success = false;
      })
      .addCase(postWebsiteProductFeature.fulfilled, (state, action) => {
        state.websiteProductFeature = getWebsiteProductFeaturePayload(
          action?.payload,
        );
        state.saving = false;
        state.success = true;
      })
      .addCase(postWebsiteProductFeature.rejected, (state, action) => {
        state.saving = false;
        state.error = action?.payload?.errorMessage || null;
        state.success = false;
      })
      .addCase(updateWebsiteProductFeature.pending, (state) => {
        state.saving = true;
        state.error = null;
        state.success = false;
      })
      .addCase(updateWebsiteProductFeature.fulfilled, (state, action) => {
        state.websiteProductFeature = getWebsiteProductFeaturePayload(
          action?.payload,
        );
        state.saving = false;
        state.success = true;
      })
      .addCase(updateWebsiteProductFeature.rejected, (state, action) => {
        state.saving = false;
        state.error = action?.payload?.errorMessage || null;
        state.success = false;
      })
      .addCase(deleteWebsiteProductFeature.pending, (state) => {
        state.deleting = true;
        state.error = null;
        state.success = false;
      })
      .addCase(deleteWebsiteProductFeature.fulfilled, (state) => {
        state.websiteProductFeature = null;
        state.deleting = false;
        state.success = true;
      })
      .addCase(deleteWebsiteProductFeature.rejected, (state, action) => {
        state.deleting = false;
        state.error = action?.payload?.errorMessage || null;
        state.success = false;
      });

    builder.addCase(getProductFeatureChild.pending, (state) => {
      state.productFeatureChildLoading = true;
      state.error = null;
      state.success = false;
    });

    builder.addCase(getProductFeatureChild.fulfilled, (state, action) => {
      state.productFeatureChildren = getProductFeatureChildItems(
        action?.payload,
      );
      state.productFeatureChildLoading = false;
      state.success = true;
    });

    builder.addCase(getProductFeatureChild.rejected, (state, action) => {
      state.productFeatureChildLoading = false;
      state.error = action?.payload?.errorMessage || null;
      state.success = false;
    });

    builder.addCase(postProductFeatureChild.pending, (state) => {
      state.productFeatureChildSaving = true;
      state.error = null;
      state.success = false;
    });

    builder.addCase(postProductFeatureChild.fulfilled, (state, action) => {
      const child = getResponsePayload(action?.payload);

      if (child && !Array.isArray(child)) {
        state.productFeatureChildren = [
          ...state.productFeatureChildren,
          child,
        ];
      }
      state.productFeatureChildSaving = false;
      state.success = true;
    });

    builder.addCase(postProductFeatureChild.rejected, (state, action) => {
      state.productFeatureChildSaving = false;
      state.error = action?.payload?.errorMessage || null;
      state.success = false;
    });

    builder.addCase(updateProductFeatureChild.pending, (state) => {
      state.productFeatureChildSaving = true;
      state.error = null;
      state.success = false;
    });

    builder.addCase(updateProductFeatureChild.fulfilled, (state, action) => {
      const child = getResponsePayload(action?.payload);

      if (child?.id) {
        state.productFeatureChildren = state.productFeatureChildren.map((item) =>
          item?.id === child.id ? child : item,
        );
      }
      state.productFeatureChildSaving = false;
      state.success = true;
    });

    builder.addCase(updateProductFeatureChild.rejected, (state, action) => {
      state.productFeatureChildSaving = false;
      state.error = action?.payload?.errorMessage || null;
      state.success = false;
    });

    builder.addCase(deleteProductFeatureChild.pending, (state) => {
      state.productFeatureChildDeleting = true;
      state.error = null;
      state.success = false;
    });

    builder.addCase(deleteProductFeatureChild.fulfilled, (state, action) => {
      const deletedId =
        action?.meta?.arg?.childId ?? action?.meta?.arg?.id ?? action?.meta?.arg;

      state.productFeatureChildren = state.productFeatureChildren.filter(
        (item) => String(item?.id) !== String(deletedId),
      );
      state.productFeatureChildDeleting = false;
      state.success = true;
    });

    builder.addCase(deleteProductFeatureChild.rejected, (state, action) => {
      state.productFeatureChildDeleting = false;
      state.error = action?.payload?.errorMessage || null;
      state.success = false;
    });

    builder.addCase(getWebsiteProductFeatureOrderProcessChild.pending, (state) => {
      state.childLoading = true;
      state.error = null;
      state.success = false;
    });

    builder.addCase(
      getWebsiteProductFeatureOrderProcessChild.fulfilled,
      (state, action) => {
        state.selectedOrderProcessChild =
          action?.payload?.response ?? action?.payload;
        state.childLoading = false;
        state.success = true;
      },
    );

    builder.addCase(
      getWebsiteProductFeatureOrderProcessChild.rejected,
      (state, action) => {
        state.childLoading = false;
        state.error = action?.payload?.errorMessage || null;
        state.success = false;
      },
    );

    builder.addCase(getOrderProcessChildByWebsiteProductFeature.pending, (state) => {
      state.childLoading = true;
      state.error = null;
      state.success = false;
    });

    builder.addCase(
      getOrderProcessChildByWebsiteProductFeature.fulfilled,
      (state, action) => {
        const items = getOrderProcessChildItems(action?.payload);

        state.orderProcessChild = items;
        if (state.websiteProductFeature) {
          state.websiteProductFeature.orderProcessChild = items;
        }
        state.childLoading = false;
        state.success = true;
      },
    );

    builder.addCase(
      getOrderProcessChildByWebsiteProductFeature.rejected,
      (state, action) => {
        state.childLoading = false;
        state.error = action?.payload?.errorMessage || null;
        state.success = false;
      },
    );

    builder.addCase(postWebsiteProductFeatureOrderProcessChild.pending, (state) => {
      state.childSaving = true;
      state.error = null;
      state.success = false;
    });

    builder.addCase(
      postWebsiteProductFeatureOrderProcessChild.fulfilled,
      (state, action) => {
        const child = getResponsePayload(action?.payload);

        if (child && !Array.isArray(child)) {
          state.orderProcessChild = [...state.orderProcessChild, child];
          if (state.websiteProductFeature) {
            state.websiteProductFeature.orderProcessChild =
              state.orderProcessChild;
          }
        }
        state.childSaving = false;
        state.success = true;
      },
    );

    builder.addCase(
      postWebsiteProductFeatureOrderProcessChild.rejected,
      (state, action) => {
        state.childSaving = false;
        state.error = action?.payload?.errorMessage || null;
        state.success = false;
      },
    );

    builder.addCase(updateWebsiteProductFeatureOrderProcessChild.pending, (state) => {
      state.childSaving = true;
      state.error = null;
      state.success = false;
    });

    builder.addCase(
      updateWebsiteProductFeatureOrderProcessChild.fulfilled,
      (state, action) => {
        const child = getResponsePayload(action?.payload);

        if (child?.id) {
          state.orderProcessChild = state.orderProcessChild.map((item) =>
            item?.id === child.id ? child : item,
          );
          if (state.websiteProductFeature) {
            state.websiteProductFeature.orderProcessChild =
              state.orderProcessChild;
          }
        }
        state.childSaving = false;
        state.success = true;
      },
    );

    builder.addCase(
      updateWebsiteProductFeatureOrderProcessChild.rejected,
      (state, action) => {
        state.childSaving = false;
        state.error = action?.payload?.errorMessage || null;
        state.success = false;
      },
    );

    builder.addCase(deleteWebsiteProductFeatureOrderProcessChild.pending, (state) => {
      state.childDeleting = true;
      state.error = null;
      state.success = false;
    });

    builder.addCase(
      deleteWebsiteProductFeatureOrderProcessChild.fulfilled,
      (state, action) => {
        const deletedId = action?.meta?.arg?.id ?? action?.meta?.arg;

        state.orderProcessChild = state.orderProcessChild.filter(
          (item) => String(item?.id) !== String(deletedId),
        );
        if (state.websiteProductFeature) {
          state.websiteProductFeature.orderProcessChild =
            state.orderProcessChild;
        }
        state.childDeleting = false;
        state.success = true;
      },
    );

    builder.addCase(
      deleteWebsiteProductFeatureOrderProcessChild.rejected,
      (state, action) => {
        state.childDeleting = false;
        state.error = action?.payload?.errorMessage || null;
        state.success = false;
      },
    );
  },
});

export const {
  clearWebsiteProductFeatureError,
  clearWebsiteProductFeatureState,
} = websiteProductFeatureSlice.actions;

export default websiteProductFeatureSlice.reducer;
